import { useMutation } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { endpoints } from "@/core/api/endpoints";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import type {
  QrPaymentFeeQuoteRequestDto,
  QrPaymentFeeQuoteResponseDto,
} from "@/features/merchant/api/dto";

/**
 * Corps Postman `FeeQuoteRequestDto` : tous les champs du contrat, y
 * compris `transactionReference` (requis à la désérialisation). UUID
 * généré si l'appelant n'en fournit pas — le devis précède l'initiate,
 * donc pas encore de `FPT-…` wallet.
 */
const toFeeQuoteBody = (
  request: QrPaymentFeeQuoteRequestDto
): QrPaymentFeeQuoteRequestDto => ({
  productCode: request.productCode,
  providerId: request.providerId ?? null,
  customerSegment: request.customerSegment,
  feePurpose: request.feePurpose,
  amountMinor: request.amountMinor,
  currency: request.currency,
  transactionReference:
    request.transactionReference.trim() || crypto.randomUUID(),
  beneficiaryPartyId: request.beneficiaryPartyId ?? null,
  promoCode: request.promoCode ?? null,
  partyId: request.partyId ?? null,
});

/**
 * Devis de frais d'un paiement QR (`POST /fee-quotes`, fee-engine §6.5) —
 * alimente la section frais de la ShareablePreview avant initiation.
 *
 * Réplique le hook `useFeeQuote` de fee-engine via le registre
 * `@/core/api/endpoints` (règle : pas d'import croisé entre features).
 */
export const useQrPaymentFeeQuote = () =>
  useMutation({
    mutationKey: ["merchant", "fee-quotes", "qr-payment"],
    mutationFn: (body: QrPaymentFeeQuoteRequestDto) =>
      mutateIdempotent<QrPaymentFeeQuoteResponseDto>({
        scopeId: "merchant.qr.fee-quotes",
        url: endpoints.feeEngine.feeQuotes.create,
        body: toFeeQuoteBody(body),
      }),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useQrPaymentFeeQuote failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
